<template>
<div class="horizontal-two-scroll-bar">
  <div
    ref="masterScroll"
    class="scroll_master hts-scroll-area"
    @scroll="handleTopScroll"
  >
    <div class="scroll-div1" :style="{ width: boxWidth+'px' }">
    </div>
  </div>
  <div
    v-if="hasHorizontalOverflow"
    class="hts-scrollbar-rail hts-scrollbar-rail--top"
    aria-hidden="true"
  >
    <div
      class="hts-scrollbar-track"
      @mousedown="onTrackMouseDown"
    >
      <div
        class="hts-scrollbar-thumb"
        :style="thumbStyle"
        @mousedown.stop.prevent="onThumbMouseDown"
      />
    </div>
  </div>
  <div
    ref="slaveScroll"
    class="scroll_slave hts-scroll-area"
    @scroll="handleSlaveScroll"
  >
    <div class="scroll-div2" ref="boxRef">
        <slot></slot>
    </div>
  </div>
  <div
    v-if="hasHorizontalOverflow"
    class="hts-scrollbar-rail hts-scrollbar-rail--bottom"
    aria-hidden="true"
  >
    <div
      class="hts-scrollbar-track"
      @mousedown="onTrackMouseDown"
    >
      <div
        class="hts-scrollbar-thumb"
        :style="thumbStyle"
        @mousedown.stop.prevent="onThumbMouseDown"
      />
    </div>
  </div>
</div>
</template>

<script>
const MIN_THUMB_PX = 24

export default {
  data () {
    return {
      interval: null,
      boxWidth: 800,
      resizeObserver: null,
      hasHorizontalOverflow: false,
      thumbWidthPx: 0,
      thumbLeftPx: 0,
      thumbDrag: null,
    }
  },
  computed: {
    thumbStyle () {
      return {
        width: `${this.thumbWidthPx}px`,
        left: `${this.thumbLeftPx}px`,
      }
    },
  },
  methods: {
    setScrollLeft (left) {
      const master = this.$refs.masterScroll
      const slave = this.$refs.slaveScroll
      if (!master || !slave) return
      const maxScroll = Math.max(0, slave.scrollWidth - slave.clientWidth)
      const next = Math.max(0, Math.min(maxScroll, left))
      master.scrollLeft = next
      slave.scrollLeft = next
      this.updateScrollbarMetrics()
    },
    updateScrollbarMetrics () {
      this.$nextTick(() => {
        const slave = this.$refs.slaveScroll
        if (!slave) return

        const maxScroll = slave.scrollWidth - slave.clientWidth
        const nextOverflow = maxScroll > 1

        if (nextOverflow !== this.hasHorizontalOverflow) {
          this.hasHorizontalOverflow = nextOverflow
        }

        if (!nextOverflow) {
          this.thumbWidthPx = 0
          this.thumbLeftPx = 0
          this._htsLayoutAttempts = 0
          return
        }

        const track = this.$el.querySelector('.hts-scrollbar-track')
        if (!track || track.clientWidth < 1) {
          this._htsLayoutAttempts = (this._htsLayoutAttempts || 0) + 1
          if (this._htsLayoutAttempts < 20) {
            this.$nextTick(() => this.updateScrollbarMetrics())
          }
          return
        }
        this._htsLayoutAttempts = 0

        const trackW = track.clientWidth
        const ratio = slave.clientWidth / slave.scrollWidth
        this.thumbWidthPx = Math.max(MIN_THUMB_PX, Math.round(ratio * trackW))
        const range = Math.max(0, trackW - this.thumbWidthPx)
        const scrollRatio = maxScroll > 0 ? slave.scrollLeft / maxScroll : 0
        this.thumbLeftPx = Math.round(scrollRatio * range)
      })
    },
    handleTopScroll (e) {
      const slave = e.target.parentNode.querySelector('.scroll_slave')
      const master = e.target
      slave.scroll({ left: master.scrollLeft })
      this.updateScrollbarMetrics()
    },
    handleSlaveScroll (e) {
      const slave = e.target
      const master = e.target.parentNode.querySelector('.scroll_master')
      master.scroll({ left: slave.scrollLeft })
      this.updateScrollbarMetrics()
    },
    onThumbMouseDown (e) {
      const slave = this.$refs.slaveScroll
      const track = e.target.closest('.hts-scrollbar-track')
      if (!slave || !track) return
      this.thumbDrag = {
        startX: e.clientX,
        startScrollLeft: slave.scrollLeft,
        track,
      }
      document.addEventListener('mousemove', this.onThumbMouseMove)
      document.addEventListener('mouseup', this.onThumbMouseUp)
    },
    onThumbMouseMove (e) {
      if (!this.thumbDrag) return
      const slave = this.$refs.slaveScroll
      const track = this.thumbDrag.track
      if (!slave || !track) return
      const maxScroll = slave.scrollWidth - slave.clientWidth
      const trackW = track.clientWidth
      const thumbW = this.thumbWidthPx
      const range = trackW - thumbW
      if (range <= 0) return
      const delta = e.clientX - this.thumbDrag.startX
      const scrollDelta = (delta / range) * maxScroll
      this.setScrollLeft(this.thumbDrag.startScrollLeft + scrollDelta)
    },
    onThumbMouseUp () {
      this.thumbDrag = null
      document.removeEventListener('mousemove', this.onThumbMouseMove)
      document.removeEventListener('mouseup', this.onThumbMouseUp)
    },
    onTrackMouseDown (e) {
      const track = e.currentTarget
      const thumb = track.querySelector('.hts-scrollbar-thumb')
      if (thumb && (e.target === thumb || thumb.contains(e.target))) return
      const slave = this.$refs.slaveScroll
      if (!track || !slave) return
      const rect = track.getBoundingClientRect()
      const maxScroll = slave.scrollWidth - slave.clientWidth
      const trackW = track.clientWidth
      const thumbW = this.thumbWidthPx
      const range = trackW - thumbW
      if (range <= 0) return
      const x = e.clientX - rect.left - thumbW / 2
      const ratio = Math.max(0, Math.min(1, x / range))
      this.setScrollLeft(ratio * maxScroll)
    },
  },
  mounted () {
    this.interval = setInterval(() => {
      if (this.$refs.boxRef) {
        this.boxWidth = this.$refs.boxRef.clientWidth
      }
      this.updateScrollbarMetrics()
    }, 1000)

    this.updateScrollbarMetrics()

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.updateScrollbarMetrics())
      this.$nextTick(() => {
        if (this.$refs.slaveScroll) {
          this.resizeObserver.observe(this.$refs.slaveScroll)
        }
        if (this.$refs.boxRef) {
          this.resizeObserver.observe(this.$refs.boxRef)
        }
      })
    }
  },
  beforeDestroy () {
    if (this.interval) {
      clearInterval(this.interval)
    }
    this.onThumbMouseUp()
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  },
}
</script>

<style lang="scss" scoped>
.horizontal-two-scroll-bar {
  width: 100%;
}

.scroll_master, .scroll_slave {
  overflow-x: auto;
  width: 100%;
}

.scroll-div1 {
  height: 1px;
}

.scroll-div2 {
  width: fit-content;
  min-width: 100%;
}

/* Hide native scrollbars; custom rails above and below the content. */
.hts-scroll-area {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.hts-scroll-area::-webkit-scrollbar {
  display: none;
  height: 0;
  width: 0;
}

.hts-scrollbar-rail {
  width: 100%;
  box-sizing: border-box;
}

.hts-scrollbar-rail--top {
  padding: 0 4px 6px;
}

.hts-scrollbar-rail--bottom {
  padding: 6px 4px 0;
}

.hts-scrollbar-track {
  position: relative;
  height: 8px;
  border-radius: 4px;
  background-color: var(--v-gray8Color-base);
  box-sizing: border-box;
}

.hts-scrollbar-thumb {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 4px;
  background-color: var(--v-darkGrayColor-base);
  cursor: grab;
  box-sizing: border-box;
}

.hts-scrollbar-thumb:active {
  cursor: grabbing;
}
</style>
